import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { GetDeliveryResultReportsParams, GetDeliveryResultReportsResponse } from '~/types/delivery-agency.types';

/**
 * [배송 결과 리포트] 목록/서머리 조회 API
 *
 * @remarks
 * 단일 엔드포인트에서 운송장 목록(`data.items`)과 서머리(`data.summary`)를 함께 반환합니다.
 */
export const fetchDeliveryResultReports = async (
  params: GetDeliveryResultReportsParams
): Promise<GetDeliveryResultReportsResponse> => {
  const { data } = await axiosInstance.get<GetDeliveryResultReportsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_DELIVERY_RESULT_REPORTS,
    { params }
  );
  return data;
};

type UseGetDeliveryResultReportsQueryOptions = {
  /** 검색 전(null)에는 쿼리를 실행하지 않습니다 */
  params: GetDeliveryResultReportsParams | null;
  showGlobalDimOverlay?: boolean;
};

/**
 * [배송 결과 리포트] 목록/서머리 조회 Query Hook
 *
 * @remarks
 * `params`가 `null`이면 `skipToken`으로 쿼리를 비활성화합니다 (queryFn 분기에서 params가 non-null로 좁혀짐).
 */
export const useGetDeliveryResultReportsQuery = ({
  params,
  showGlobalDimOverlay = true,
}: UseGetDeliveryResultReportsQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.deliveryResultReports(params),
    queryFn: params ? () => fetchDeliveryResultReports(params) : skipToken,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
    meta: { showGlobalDimOverlay },
  });
};

/**
 * [배송 결과 리포트] 목록/서머리 조회 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다.
 */
registerMockAPI<GetDeliveryResultReportsResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/delivery-result-reports',
  label: '배송 결과 리포트/목록·서머리',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: {
          items: [
            {
              id: 1100,
              outboundCode: 'JA9LAK0133658',
              clientOrderCodes: ['ORD-20260522-0100'],
              shippingLabelNumber: '210-C0-JA9LAK0133658-0001',
              estimatedDeliveryDate: '2026-05-22',
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              reportedShippingLabelStatus: { code: 'OUTBOUND_READY_OR_PROCESSING', description: '출고 대기/진행중' },
              reportedDeliveryResults: [],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: null,
              actualDeliveryLocationType: '',
              deliveryFailureReasonCategory: { code: null, description: null },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '공동현관 비밀번호',
              receiverName: '한**',
              refineFullAddress: '서울시 성동구 **로 ***',
              deliveryCompletedImageUrl: null,
            },
            {
              id: 1001,
              outboundCode: 'JA9LAK0133660',
              clientOrderCodes: ['ORD-20260522-0001'],
              shippingLabelNumber: '210-C0-JA9LAK0133660-0001',
              estimatedDeliveryDate: '2026-05-22',
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
              reportedDeliveryResults: [{ code: 'NORMAL', description: '정상배송' }],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: null,
              actualDeliveryLocationType: '문 앞',
              deliveryFailureReasonCategory: { code: null, description: null },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '공동현관 비밀번호',
              receiverName: '홍**',
              refineFullAddress: '서울시 강남구 **로 ***',
              deliveryCompletedImageUrl: 'https://example.com/delivery-completed/1001.jpg',
            },
            {
              id: 1002,
              outboundCode: 'JA9LAK0133661',
              clientOrderCodes: ['ORD-20260522-0002', 'ORD-20260522-0003'],
              shippingLabelNumber: '210-C0-JA9LAK0133661-0001',
              estimatedDeliveryDate: '2026-05-22',
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_FAILED', description: '배송 실패' },
              reportedDeliveryResults: [{ code: 'NON_DELIVERED', description: '미배송' }],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: '1층    경비실', // 내부 연속 공백 노출 확인용
              actualDeliveryLocationType: '문 앞',
              deliveryFailureReasonCategory: { code: 'NO_ENTRY', description: '출입 불가' },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '경비실 호출',
              receiverName: '김**',
              refineFullAddress: '서울시 송파구 **로 ***',
              deliveryCompletedImageUrl: null,
            },
            {
              id: 1003,
              outboundCode: 'JA9LAK0133662',
              clientOrderCodes: ['ORD-20260522-0004'],
              shippingLabelNumber: '210-C0-JA9LAK0133662-0001',
              estimatedDeliveryDate: '2026-05-22',
              deliveryType: { code: 'NIGHT', description: '저녁배송' },
              reportedShippingLabelStatus: { code: 'ON_THE_WAY', description: '배송 중' },
              reportedDeliveryResults: [],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: null,
              actualDeliveryLocationType: '',
              deliveryFailureReasonCategory: { code: null, description: null },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '자유출입',
              receiverName: '이**',
              refineFullAddress: '서울시 마포구 **로 ***',
              deliveryCompletedImageUrl: null,
            },
            {
              id: 1004,
              outboundCode: 'JA9LAK0133663',
              clientOrderCodes: ['ORD-20260522-0005'],
              shippingLabelNumber: '210-C0-JA9LAK0133663-0001',
              estimatedDeliveryDate: '2026-05-22',
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
              reportedDeliveryResults: [{ code: 'ALTERNATIVE_DELIVERY', description: '대응배송' }],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: '702호   (문   앞   벽면   쪽에   붙여   주세요)', // 길어서 truncate → 툴팁 원본 노출 확인용
              actualDeliveryLocationType: '공동현관(대문) 앞',
              deliveryFailureReasonCategory: { code: null, description: null },
              changedDeliveryMethodType: { code: 'NO_ENTRY', description: '출입 불가' },
              entranceMethod: '공동현관 비밀번호',
              receiverName: '박**',
              refineFullAddress: '경기도 성남시 분당구 **로 ***',
              deliveryCompletedImageUrl: 'https://example.com/delivery-completed/1004.jpg',
            },
            {
              id: 1005,
              outboundCode: 'JA9LAK0133664',
              clientOrderCodes: ['ORD-20260523-0001'],
              shippingLabelNumber: '210-C0-JA9LAK0133664-0001',
              estimatedDeliveryDate: '2026-05-23',
              deliveryType: { code: 'DAY', description: '하루배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
              reportedDeliveryResults: [{ code: 'DELAYED', description: '지연배송' }],
              deliveryLocation: '택배 수령실',
              deliveryLocationDetail: '  택배함  3번  ', // 앞뒤·내부 공백 보존 확인용
              actualDeliveryLocationType: '택배 수령실',
              deliveryFailureReasonCategory: { code: null, description: null },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '자유출입',
              receiverName: '최**',
              refineFullAddress: '서울시 강서구 **로 ***',
              deliveryCompletedImageUrl: 'https://example.com/delivery-completed/1005.jpg',
            },
            {
              id: 1006,
              outboundCode: 'JA9LAK0133665',
              clientOrderCodes: ['ORD-20260523-0002'],
              shippingLabelNumber: '210-C0-JA9LAK0133665-0001',
              estimatedDeliveryDate: '2026-05-23',
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_CANCELED', description: '배송 취소' },
              reportedDeliveryResults: [],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: null,
              actualDeliveryLocationType: '',
              deliveryFailureReasonCategory: { code: 'CUSTOMER_REQUEST', description: '고객 요청' },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '공동현관 비밀번호',
              receiverName: '정**',
              refineFullAddress: '서울시 동작구 **로 ***',
              deliveryCompletedImageUrl: null,
            },
            {
              id: 1007,
              outboundCode: 'JA9LAK0133666',
              clientOrderCodes: ['ORD-20260523-0003', 'ORD-20260523-0004', 'ORD-20260523-0005'],
              shippingLabelNumber: '210-C0-JA9LAK0133666-0001',
              estimatedDeliveryDate: '2026-05-23',
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
              reportedDeliveryResults: [
                { code: 'CONVERTED_DELIVERY', description: '전환배송' },
                { code: 'DELAYED', description: '지연배송' },
              ],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: null,
              actualDeliveryLocationType: '문 앞',
              deliveryFailureReasonCategory: { code: null, description: null },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '경비실 호출',
              receiverName: '강**',
              refineFullAddress: '인천시 연수구 **로 ***',
              deliveryCompletedImageUrl: 'https://example.com/delivery-completed/1007.jpg',
            },
            {
              id: 1008,
              outboundCode: 'JA9LAK0133667',
              clientOrderCodes: ['ORD-20260524-0001'],
              shippingLabelNumber: '210-C0-JA9LAK0133667-0001',
              estimatedDeliveryDate: '2026-05-24',
              deliveryType: { code: 'NIGHT', description: '저녁배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_FAILED', description: '배송 실패' },
              reportedDeliveryResults: [{ code: 'NON_DELIVERED', description: '미배송' }],
              deliveryLocation: '기타',
              deliveryLocationDetail: '지하 1층\n경비실 앞에 놔 주세요', // 레거시 개행: 셀은 단일 라인, 툴팁은 개행 노출
              actualDeliveryLocationType: '',
              deliveryFailureReasonCategory: { code: 'ADDRESS_ERROR', description: '주소 오류' },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '경비실 호출',
              receiverName: '윤**',
              refineFullAddress: '경기도 고양시 일산동구 **로 ***',
              deliveryCompletedImageUrl: null,
            },
            {
              id: 1009,
              outboundCode: 'JA9LAK0133668',
              clientOrderCodes: ['ORD-20260524-0002'],
              shippingLabelNumber: '210-C0-JA9LAK0133668-0001',
              estimatedDeliveryDate: '2026-05-24',
              deliveryType: { code: 'DAWN', description: '샛별배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_COMPLETED', description: '배송 완료' },
              reportedDeliveryResults: [
                { code: 'ALTERNATIVE_DELIVERY', description: '대응배송' },
                { code: 'DELAYED', description: '지연배송' },
              ],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: '현관 앞',
              actualDeliveryLocationType: '택배 수령실',
              deliveryFailureReasonCategory: { code: null, description: null },
              changedDeliveryMethodType: { code: 'BAD_WEATHER', description: '기상 악화' },
              entranceMethod: '자유출입',
              receiverName: '장**',
              refineFullAddress: '서울시 노원구 **로 ***',
              deliveryCompletedImageUrl: 'https://example.com/delivery-completed/1009.jpg',
            },
            {
              id: 1010,
              outboundCode: 'JA9LAK0133669',
              clientOrderCodes: ['ORD-20260524-0003'],
              shippingLabelNumber: '210-C0-JA9LAK0133669-0001',
              estimatedDeliveryDate: '2026-05-24',
              deliveryType: { code: 'DAY', description: '하루배송' },
              reportedShippingLabelStatus: { code: 'DELIVERY_FAILED', description: '배송 실패' },
              reportedDeliveryResults: [{ code: 'NON_DELIVERED', description: '미배송' }],
              deliveryLocation: '문 앞',
              deliveryLocationDetail: null,
              actualDeliveryLocationType: '',
              deliveryFailureReasonCategory: { code: 'SELLER_NOT_SHIPPED', description: '판매자 미출고' },
              changedDeliveryMethodType: { code: null, description: null },
              entranceMethod: '공동현관 비밀번호',
              receiverName: '임**',
              refineFullAddress: '서울시 서초구 **로 ***',
              deliveryCompletedImageUrl: null,
            },
          ],
          summary: {
            totalCount: 1234,
            shippingLabelStatusCounts: [
              { code: 'OUTBOUND_READY_OR_PROCESSING', description: '출고 대기/진행중', count: 100 },
              { code: 'ON_THE_WAY', description: '배송 중', count: 120 },
              { code: 'DELIVERY_COMPLETED', description: '배송 완료', count: 1000 },
              { code: 'DELIVERY_CANCELED', description: '배송 취소', count: 14 },
              { code: 'DELIVERY_FAILED', description: '배송 실패', count: 100 },
            ],
            deliveryResultCounts: [
              { code: 'NORMAL', description: '정상배송', count: 1000 },
              { code: 'ALTERNATIVE_DELIVERY', description: '대응배송', count: 80 },
              { code: 'DELAYED', description: '지연배송', count: 54 },
              { code: 'CONVERTED_DELIVERY', description: '전환배송', count: 20 },
              { code: 'NON_DELIVERED', description: '미배송', count: 80 },
            ],
          },
        },
        pagination: {
          cursor: 1,
          listView: 50,
          listTotal: 1234,
        },
      },
    },
    {
      name: '데이터 없음',
      response: {
        data: {
          items: [],
          summary: {
            totalCount: 0,
            shippingLabelStatusCounts: [
              { code: 'OUTBOUND_READY_OR_PROCESSING', description: '출고 대기/진행중', count: 0 },
              { code: 'ON_THE_WAY', description: '배송 중', count: 0 },
              { code: 'DELIVERY_COMPLETED', description: '배송 완료', count: 0 },
              { code: 'DELIVERY_CANCELED', description: '배송 취소', count: 0 },
              { code: 'DELIVERY_FAILED', description: '배송 실패', count: 0 },
            ],
            deliveryResultCounts: [
              { code: 'NORMAL', description: '정상배송', count: 0 },
              { code: 'ALTERNATIVE_DELIVERY', description: '대응배송', count: 0 },
              { code: 'DELAYED', description: '지연배송', count: 0 },
              { code: 'CONVERTED_DELIVERY', description: '전환배송', count: 0 },
              { code: 'NON_DELIVERED', description: '미배송', count: 0 },
            ],
          },
        },
        pagination: {
          cursor: 1,
          listView: 50,
          listTotal: 0,
        },
      },
    },
  ],
});
